import { parse, format } from "date-fns";
import { FTLBooking } from "../../types";
import truckSlugs from "../../config/smartsuite/trucks";

const RECORD_ID_PATTERN = /^[a-f0-9]{24}$/;
const EXPECTED_PARTS = 10;
const DATE_SUFFIX_PATTERN = /(\w{3},\s\w{3}\s\d{1,2},\s\d{4})$/;

export function splitAvailableBookingEntries(
  raw: string | null | undefined
): string[] {
  return String(raw || "")
    .split(" | ")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function splitBookedEventEntries(
  raw: string | null | undefined
): string[] {
  return String(raw || "")
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseDateFromTitle(title: string): string | null {
  const match = title.match(DATE_SUFFIX_PATTERN);
  if (!match) {
    return null;
  }

  const parsed = parse(match[1], "EEE, MMM dd, yyyy", new Date());
  if (isNaN(parsed.getTime())) {
    return null;
  }

  return format(parsed, "yyyy-MM-dd'T'00:00:00'Z'");
}

export function parseBookingEntry(entry: string): FTLBooking | null {
  const parts = entry.split("~");
  if (parts.length !== EXPECTED_PARTS) {
    return null;
  }

  const [event, id, title, address, addressLink, , startTime, endTime, , truckIds] =
    parts;

  if (!RECORD_ID_PATTERN.test(event) || !RECORD_ID_PATTERN.test(id)) {
    return null;
  }

  const date = parseDateFromTitle(title);
  if (!date) {
    return null;
  }

  const scheduledTrucks = truckIds.split("^").filter(Boolean);
  if (scheduledTrucks.some((truckId) => !RECORD_ID_PATTERN.test(truckId))) {
    return null;
  }

  return {
    id,
    title,
    event,
    date,
    startTime,
    endTime,
    scheduledTrucks,
    address,
    addressLink,
    maxTrucks: NaN,
    message: "",
  };
}

export function parseTruckBookingFields(
  truckData: Record<string, unknown>
): FTLBooking[] {
  const available = splitAvailableBookingEntries(
    truckData[truckSlugs["Scheduling Tool Available Bookings"]] as string
  );
  const booked = splitBookedEventEntries(
    truckData[truckSlugs["Scheduling Tool Booked Events"]] as string
  );

  const byId = new Map<string, FTLBooking>();

  for (const entry of available) {
    const booking = parseBookingEntry(entry);
    if (booking?.id) {
      byId.set(booking.id, booking);
    }
  }

  for (const entry of booked) {
    const booking = parseBookingEntry(entry);
    if (booking?.id) {
      byId.set(booking.id, booking);
    }
  }

  return Array.from(byId.values());
}

export function applyPendingScheduledTrucks(
  bookings: FTLBooking[],
  pending: Map<string, string>
): FTLBooking[] {
  if (pending.size === 0) {
    return bookings;
  }

  return bookings.map((booking) => {
    if (!booking.id) {
      return booking;
    }

    const truckId = pending.get(booking.id);
    if (!truckId) {
      return booking;
    }

    if (booking.scheduledTrucks.includes(truckId)) {
      pending.delete(booking.id);
      return booking;
    }

    return {
      ...booking,
      scheduledTrucks: [...booking.scheduledTrucks, truckId],
    };
  });
}

export function markBookingScheduled(
  bookings: FTLBooking[],
  bookingId: string,
  truckId: string
): FTLBooking[] {
  return bookings.map((booking) => {
    if (booking.id !== bookingId || booking.scheduledTrucks.includes(truckId)) {
      return booking;
    }
    return {
      ...booking,
      scheduledTrucks: [...booking.scheduledTrucks, truckId],
    };
  });
}
